/**
 * The notice at the top of every route (login included) when the translations of the active language could
 * not be loaded (#4114). It is the error state the person was missing: without it a screen that shows
 * English or humanised keys looks like a bug of the page. It leaves by itself once the texts arrive.
 */

import React, { useSyncExternalStore } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { appTranslationLoadStatus } from '../translation-load-status'

const MESSAGE_KEY = {
  degraded: 'i18nDegraded.message',
  retrying: 'i18nDegraded.loading',
  'still-failing': 'i18nDegraded.stillFailing',
} as const

export const TranslationsDegradedNotice: React.FC = () => {
  // `common` is a critical namespace: its copy is seeded from the bundle even when the server is down.
  const { t } = useTranslation('common')
  const phase = useSyncExternalStore(
    appTranslationLoadStatus.subscribe,
    appTranslationLoadStatus.getSnapshot,
  )

  const degraded = phase !== 'ok'
  const retrying = phase === 'retrying'

  // The live region is always mounted (empty while `ok`) so assistive tech registers it before the text
  // enters; a region inserted together with its content is often not announced.
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="translations-degraded-status"
      className="w-full"
    >
      {degraded && (
        <div
          data-testid="translations-degraded-notice"
          className="w-full bg-amber-600 text-white px-4 py-2 shadow-lg z-50 [html[data-theme=light]_&]:bg-amber-700"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium">
              <span aria-hidden="true">⚠ </span>
              {t(MESSAGE_KEY[phase])}
            </p>
            {/* aria-disabled (not `disabled`) keeps the focused button focusable while it retries. */}
            <button
              type="button"
              onClick={() => {
                if (retrying) return
                void appTranslationLoadStatus.retry()
              }}
              aria-disabled={retrying}
              aria-busy={retrying}
              title={retrying ? t('i18nDegraded.loading') : undefined}
              className="px-4 py-1 bg-white text-amber-800 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm aria-disabled:opacity-70 aria-disabled:cursor-not-allowed"
            >
              {retrying ? t('i18nDegraded.retrying') : t('i18nDegraded.retry')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
